import { useState, useMemo, type FormEvent } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  IoArrowBack,
  IoSaveOutline,
  IoOpenOutline,
  IoCashOutline,
  IoPricetagOutline,
  IoCheckmarkCircleOutline,
  IoAlertCircleOutline,
} from 'react-icons/io5';
import { api, type ProductDetail, type ProductUpdatePayload } from '../api';
import { useAuth } from '../auth';
import { Button } from '../design';

export function ProductDetailPage() {
  const { productId } = useParams<{ productId: string }>();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const qc = useQueryClient();

  const { data: p, isLoading, error } = useQuery({
    queryKey: ['product', productId],
    queryFn: () => api.productGet(productId!),
    enabled: !!productId,
  });

  if (isLoading) return <div className="card">Загрузка…</div>;
  if (error) return <div className="card" style={{ color: 'var(--red)' }}>{(error as Error).message}</div>;
  if (!p) return <div className="card">Товар не найден</div>;

  return (
    <>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Link to="/products" className="btn"><IoArrowBack /> К списку</Link>
          <div>
            <h2 style={{ marginBottom: 4 }}>{p.name}</h2>
            <div className="muted" style={{ fontSize: 13 }}>
              ID: <code>{p.productId}</code> · Артикул: <code>{p.offerId}</code>{p.sku && <> · SKU: <code>{p.sku}</code></>}
            </div>
          </div>
        </div>
        <a
          href={`https://www.ozon.ru/product/${p.sku ?? p.productId}/`}
          target="_blank"
          rel="noreferrer"
          className="btn"
        >
          <IoOpenOutline /> Открыть на Ozon
        </a>
      </div>

      {/* Top row: image + price + stock */}
      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 24, marginBottom: 24 }}>
        <div className="card" style={{ padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
          {p.primaryImage ? (
            <img src={p.primaryImage} alt="" style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 6 }} />
          ) : (
            <div className="muted" style={{ textAlign: 'center', fontSize: 12 }}>Без изображения</div>
          )}
        </div>

        <div className="stat-grid">
          <div className="stat">
            <div className="stat-label">Наша цена</div>
            <div className="stat-value">{p.priceNow?.price ? `${Number(p.priceNow.price).toFixed(0)} ₽` : '—'}</div>
            {p.priceNow?.oldPrice && Number(p.priceNow.oldPrice) > 0 && (
              <div className="stat-sub" style={{ textDecoration: 'line-through' }}>{Number(p.priceNow.oldPrice).toFixed(0)} ₽</div>
            )}
          </div>
          <div className="stat">
            <div className="stat-label">Маркет. цена</div>
            <div className="stat-value">{p.priceNow?.marketingPrice && Number(p.priceNow.marketingPrice) > 0 ? `${Number(p.priceNow.marketingPrice).toFixed(0)} ₽` : '—'}</div>
            <div className="stat-sub">с учётом акций Ozon</div>
          </div>
          <div className="stat">
            <div className="stat-label">Индекс Ozon</div>
            <div className="stat-value" style={{ fontSize: 18 }}>
              {p.priceNow?.colorIndex ? <><span className={`color-dot ${p.priceNow.colorIndex}`} /> {p.priceNow.colorIndex}</> : '—'}
            </div>
            <div className="stat-sub">vs другие площадки</div>
          </div>
          <div className="stat">
            <div className="stat-label">Остаток</div>
            <div className="stat-value" style={{ color: p.stocks.length === 0 ? 'var(--text-muted)' : 'var(--green)' }}>
              {p.stocks.reduce((sum, s) => sum + s.present - s.reserved, 0)}
            </div>
            <div className="stat-sub">{p.stocks.length} складов · FBO/FBS</div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 24 }}>
        <EditCard product={p} isAdmin={isAdmin} onSaved={() => qc.invalidateQueries({ queryKey: ['product', productId] })} />
        <RrpCard product={p} isAdmin={isAdmin} onSaved={() => qc.invalidateQueries({ queryKey: ['product', productId] })} />
        <PriceUpdateCard product={p} isAdmin={isAdmin} onSaved={() => qc.invalidateQueries({ queryKey: ['product', productId] })} />
      </div>

      {/* Showcase prices (from BFF agent) */}
      {p.priceNow?.bffMarketingOaPrice && <ShowcasePricesBlock priceNow={p.priceNow} />}

      {/* Competitor data */}
      <div className="card" style={{ marginBottom: 24, padding: 20 }}>
        <h3 style={{ marginTop: 0, marginBottom: 16 }}>Цены конкурентов (агрегированные от Ozon)</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          <CompetitorBlock label="Внешние площадки" subtitle="WB, Я.Маркет и др." min={p.priceNow?.extMinPrice} index={p.priceNow?.extPriceIndex} ourPrice={p.priceNow?.price} />
          <CompetitorBlock label="Другие на Ozon" subtitle="та же карточка" min={p.priceNow?.ozonMinPrice} index={p.priceNow?.ozonPriceIndex} ourPrice={p.priceNow?.price} />
          <CompetitorBlock label="Свои на МП" subtitle="свои карточки в WB/ЯМ" min={p.priceNow?.mpMinPrice} index={p.priceNow?.mpPriceIndex} ourPrice={p.priceNow?.price} />
        </div>
      </div>

      {/* Стратегии товара */}
      {p.strategies.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div style={{ padding: '20px 20px 0' }}>
            <h3 style={{ margin: 0 }}>Стратегии репрайсера</h3>
          </div>
          <table className="products" style={{ marginTop: 12 }}>
            <thead><tr><th>Стратегия</th><th>Min ₽</th><th>Max ₽</th><th>Статус</th></tr></thead>
            <tbody>
              {p.strategies.map((s) => (
                <tr key={s.strategyId}>
                  <td><Link to={`/strategies/${s.strategyId}`}>{s.strategyName}</Link></td>
                  <td className="num">{s.minPrice ? `${Number(s.minPrice).toFixed(0)} ₽` : '—'}</td>
                  <td className="num">{s.maxPrice ? `${Number(s.maxPrice).toFixed(0)} ₽` : '—'}</td>
                  <td>{s.enabled ? <span className="badge badge-success">актив</span> : <span className="badge">выкл</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Полный список атрибутов */}
      {p.attributes && p.attributes.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div style={{ padding: '20px 20px 0' }}>
            <h3 style={{ margin: 0 }}>Все атрибуты ({p.attributes.length})</h3>
            {p.attributesUpdatedAt && <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>обновлено {new Date(p.attributesUpdatedAt).toLocaleString('ru-RU')}</div>}
          </div>
          <AttributesTable attributes={p.attributes} />
        </div>
      )}

      {/* История цен */}
      <div className="card">
        <div style={{ padding: '20px 20px 0' }}>
          <h3 style={{ margin: 0 }}>История изменений цены (последние 50)</h3>
        </div>
        {p.priceHistory.length === 0 ? <div className="empty">История пуста.</div> : (
          <table className="products" style={{ marginTop: 12 }}>
            <thead><tr><th>Когда</th><th>Старая</th><th>Новая</th><th>Источник</th><th>Причина</th></tr></thead>
            <tbody>
              {p.priceHistory.map((h) => (
                <tr key={h.id}>
                  <td className="muted" style={{ fontSize: 12 }}>{new Date(h.appliedAt).toLocaleString('ru-RU')}</td>
                  <td className="num">{h.oldPrice ? `${Number(h.oldPrice).toFixed(0)} ₽` : '—'}</td>
                  <td className="num"><strong>{Number(h.newPrice).toFixed(0)} ₽</strong></td>
                  <td><SourceBadge source={h.source} /></td>
                  <td className="muted" style={{ fontSize: 12 }}>{h.reason ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function ShowcasePricesBlock({ priceNow }: { priceNow: NonNullable<ProductDetail['priceNow']> }) {
  const fmt = (v: string | null | undefined) => v != null && Number(v) > 0 ? `${Number(v).toFixed(0)} ₽` : '—';
  const ourPrice = Number(priceNow.price ?? 0);
  const subsidy = priceNow.ozonSubsidyPct ? Number(priceNow.ozonSubsidyPct) : null;
  const actions = priceNow.marketingActionsJson ?? [];
  const schemes: { key: string; label: string; mp: string | null; oa: string | null }[] = [
    { key: 'FBO', label: 'FBO', mp: priceNow.marketingPriceFbo, oa: priceNow.marketingOaPriceFbo },
    { key: 'FBS', label: 'FBS', mp: priceNow.marketingPriceFbs, oa: priceNow.marketingOaPriceFbs },
    { key: 'RFBS', label: 'realFBS', mp: priceNow.marketingPriceRfbs, oa: priceNow.marketingOaPriceRfbs },
  ];

  return (
    <div className="card" style={{ marginBottom: 24, padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Витринные цены покупателя</h3>
        {priceNow.bffSyncedAt && (
          <span className="muted" style={{ fontSize: 12 }}>обновлено {new Date(priceNow.bffSyncedAt).toLocaleString('ru-RU')}</span>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
        <div className="stat">
          <div className="stat-label">Наша цена</div>
          <div className="stat-value">{fmt(priceNow.price)}</div>
          <div className="stat-sub">до скидок Ozon</div>
        </div>
        <div className="stat">
          <div className="stat-label">С акциями Ozon</div>
          <div className="stat-value">{fmt(priceNow.bffMarketingPrice)}</div>
          <div className="stat-sub">видит покупатель</div>
        </div>
        <div className="stat" style={{ background: '#ecfdf5', borderColor: '#a7f3d0' }}>
          <div className="stat-label" style={{ color: '#065f46' }}>С Ozon Картой</div>
          <div className="stat-value" style={{ color: '#065f46' }}>{fmt(priceNow.bffMarketingOaPrice)}</div>
          <div className="stat-sub" style={{ color: '#047857' }}>максимальная скидка</div>
        </div>
        <div className="stat">
          <div className="stat-label">Субсидия Озон</div>
          <div className="stat-value">{subsidy != null ? `${subsidy.toFixed(1)}%` : '—'}</div>
          <div className="stat-sub">
            {subsidy != null && ourPrice > 0 ? `−${(ourPrice * subsidy / 100).toFixed(0)} ₽` : ''}
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 12, fontSize: 13, fontWeight: 500 }}>По схемам доставки:</div>
      <table className="products" style={{ marginBottom: 16 }}>
        <thead><tr><th>Схема</th><th className="num">Без Ozon Карты</th><th className="num">С Ozon Картой</th><th className="num">Скидка Карты</th></tr></thead>
        <tbody>
          {schemes.map((s) => {
            const mp = s.mp ? Number(s.mp) : null;
            const oa = s.oa ? Number(s.oa) : null;
            const diff = (mp && oa) ? mp - oa : null;
            return (
              <tr key={s.key}>
                <td><strong>{s.label}</strong></td>
                <td className="num">{fmt(s.mp)}</td>
                <td className="num" style={{ color: oa ? '#065f46' : undefined, fontWeight: oa ? 600 : undefined }}>{fmt(s.oa)}</td>
                <td className="num muted">{diff != null ? `−${diff.toFixed(0)} ₽` : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {actions.length > 0 && (
        <details>
          <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>Действующие акции ({actions.length})</summary>
          <table className="products" style={{ marginTop: 12 }}>
            <thead><tr><th>Название</th><th>Тип</th><th>Действует до</th></tr></thead>
            <tbody>
              {actions.map((a, i) => (
                <tr key={a.id ?? i}>
                  <td>{a.name ?? '—'}</td>
                  <td><span className="badge">{a.is_seller_action ? 'продавец' : 'Озон'}</span></td>
                  <td className="muted">{a.date_to ? new Date(a.date_to).toLocaleDateString('ru-RU') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}

function EditCard({ product: p, isAdmin, onSaved }: { product: ProductDetail; isAdmin: boolean; onSaved: () => void }) {
  const [form, setForm] = useState<ProductUpdatePayload>({
    brandOfficial: p.brandOfficial,
    category: p.category,
    model: p.model,
    tireSize: p.tireSize,
    tireSeason: p.tireSeason,
    diskSize: p.diskSize,
    diskPcd: p.diskPcd,
    diskEt: p.diskEt,
    diskDia: p.diskDia,
    diskColor: p.diskColor,
  });
  const [success, setSuccess] = useState(false);

  const save = useMutation({
    mutationFn: () => api.productUpdate(p.productId, form),
    onSuccess: () => { setSuccess(true); onSaved(); setTimeout(() => setSuccess(false), 2000); },
  });

  const onSubmit = (e: FormEvent) => { e.preventDefault(); save.mutate(); };
  const isTire = (form.category ?? p.category) === 'Шины';
  const isDisk = (form.category ?? p.category) === 'Диск';

  return (
    <form onSubmit={onSubmit} className="card" style={{ padding: 20 }}>
      <h3 style={{ marginTop: 0, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        <IoPricetagOutline /> Атрибуты
      </h3>
      <FieldRow label="Бренд" value={form.brandOfficial} onChange={(v) => setForm({ ...form, brandOfficial: v })} disabled={!isAdmin} />
      <FieldRow label="Категория" value={form.category} onChange={(v) => setForm({ ...form, category: v })} disabled={!isAdmin} />
      <FieldRow label="Модель" value={form.model} onChange={(v) => setForm({ ...form, model: v })} disabled={!isAdmin} />
      {isTire && (
        <>
          <FieldRow label="Размер шины (185/65 R15)" value={form.tireSize} onChange={(v) => setForm({ ...form, tireSize: v })} disabled={!isAdmin} />
          <FieldRow label="Сезон" value={form.tireSeason} onChange={(v) => setForm({ ...form, tireSeason: v })} disabled={!isAdmin} />
        </>
      )}
      {isDisk && (
        <>
          <FieldRow label="Размер диска" value={form.diskSize} onChange={(v) => setForm({ ...form, diskSize: v })} disabled={!isAdmin} />
          <FieldRow label="PCD" value={form.diskPcd} onChange={(v) => setForm({ ...form, diskPcd: v })} disabled={!isAdmin} />
          <FieldRow label="ET" value={form.diskEt} onChange={(v) => setForm({ ...form, diskEt: v })} disabled={!isAdmin} />
          <FieldRow label="DIA" value={form.diskDia} onChange={(v) => setForm({ ...form, diskDia: v })} disabled={!isAdmin} />
          <FieldRow label="Цвет" value={form.diskColor} onChange={(v) => setForm({ ...form, diskColor: v })} disabled={!isAdmin} />
        </>
      )}
      {isAdmin && (
        <Button type="submit" variant="primary" loading={save.isPending} style={{ marginTop: 8 }} leftIcon={<IoSaveOutline />}>
          Сохранить
        </Button>
      )}
      {success && <div style={{ color: 'var(--green)', fontSize: 13, marginTop: 8 }}>✓ Сохранено</div>}
      {save.error && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 8 }}>{(save.error as Error).message}</div>}
    </form>
  );
}

function RrpCard({ product: p, isAdmin, onSaved }: { product: ProductDetail; isAdmin: boolean; onSaved: () => void }) {
  const [rrp, setRrp] = useState<string>(p.rrpPrice ? String(p.rrpPrice) : '');
  const [enforce, setEnforce] = useState(p.rrpEnforceEnabled);

  const save = useMutation({
    mutationFn: () => api.productUpdate(p.productId, {
      rrpPrice: rrp ? Number(rrp) : null,
      rrpEnforceEnabled: enforce,
    }),
    onSuccess: () => onSaved(),
  });

  return (
    <div className="card" style={{ padding: 20 }}>
      <h3 style={{ marginTop: 0, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        <IoCashOutline /> РРЦ-защита
      </h3>
      <div style={{ marginBottom: 12 }}>
        <div className="filter-field-label" style={{ marginBottom: 6 }}>РРЦ (₽)</div>
        <input
          className="input"
          type="number"
          min={0}
          step="0.01"
          value={rrp}
          onChange={(e) => setRrp(e.target.value)}
          disabled={!isAdmin}
          placeholder="Оставь пустым = удалить"
        />
        {p.rrpUpdatedAt && (
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
            обновлено {new Date(p.rrpUpdatedAt).toLocaleString('ru-RU')}
          </div>
        )}
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: isAdmin ? 'pointer' : 'default' }}>
        <input type="checkbox" checked={enforce} onChange={(e) => setEnforce(e.target.checked)} disabled={!isAdmin} />
        <span style={{ fontSize: 13 }}>Включить защиту от Ozon-скидок</span>
      </label>
      {p.rrpViolation && (
        <div style={{ background: 'var(--red-soft)', color: '#991b1b', padding: 10, borderRadius: 6, fontSize: 12, marginBottom: 12 }}>
          <strong style={{ display: 'flex', alignItems: 'center', gap: 4 }}><IoAlertCircleOutline /> Нарушение РРЦ</strong>
          <div style={{ marginTop: 4 }}>
            Фактическая клиентская: <strong>{p.rrpViolation.effective} ₽</strong> — ниже РРЦ на {(p.rrpViolation.gapPct * 100).toFixed(1)}%
          </div>
        </div>
      )}
      {isAdmin && (
        <Button onClick={() => save.mutate()} variant="primary" loading={save.isPending} leftIcon={<IoSaveOutline />}>
          Сохранить РРЦ
        </Button>
      )}
      {save.isSuccess && <div style={{ color: 'var(--green)', fontSize: 13, marginTop: 8 }}>✓ Сохранено</div>}
      {save.error && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 8 }}>{(save.error as Error).message}</div>}
    </div>
  );
}

function PriceUpdateCard({ product: p, isAdmin, onSaved }: { product: ProductDetail; isAdmin: boolean; onSaved: () => void }) {
  const currentPrice = p.priceNow?.price ? Number(p.priceNow.price) : 0;
  const [price, setPrice] = useState(currentPrice.toString());
  const [oldPrice, setOldPrice] = useState('');
  const [minPrice, setMinPrice] = useState('');

  const update = useMutation({
    mutationFn: () => api.productSetPrice(p.productId, {
      price: Number(price),
      oldPrice: oldPrice ? Number(oldPrice) : undefined,
      minPrice: minPrice ? Number(minPrice) : undefined,
      reason: 'manual via product detail page',
    }),
    onSuccess: () => onSaved(),
  });

  return (
    <div className="card" style={{ padding: 20 }}>
      <h3 style={{ marginTop: 0, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        <IoCashOutline /> Установить цену вручную
      </h3>
      <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
        Запишет новую цену напрямую в Ozon (<code>/v1/product/import/prices</code>) и зафиксирует в логе.
      </div>
      <div style={{ marginBottom: 10 }}>
        <div className="filter-field-label" style={{ marginBottom: 4 }}>Цена (₽)</div>
        <input className="input" type="number" min={0} step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} disabled={!isAdmin} />
      </div>
      <div style={{ marginBottom: 10 }}>
        <div className="filter-field-label" style={{ marginBottom: 4 }}>Старая цена (опц.)</div>
        <input className="input" type="number" min={0} step="0.01" value={oldPrice} onChange={(e) => setOldPrice(e.target.value)} disabled={!isAdmin} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <div className="filter-field-label" style={{ marginBottom: 4 }}>Min price (опц.)</div>
        <input className="input" type="number" min={0} step="0.01" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} disabled={!isAdmin} />
      </div>
      {isAdmin && (
        <Button variant="primary" loading={update.isPending} disabled={!price} onClick={() => {
          if (confirm(`Установить цену ${price} ₽?`)) update.mutate();
        }} leftIcon={<IoCheckmarkCircleOutline />}>
          Применить в Ozon
        </Button>
      )}
      {update.data?.updated && <div style={{ color: 'var(--green)', fontSize: 13, marginTop: 8 }}>✓ Цена обновлена в Ozon</div>}
      {update.error && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 8 }}>{(update.error as Error).message}</div>}
    </div>
  );
}

function FieldRow({ label, value, onChange, disabled }: { label: string; value: string | null | undefined; onChange: (v: string | null) => void; disabled?: boolean }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div className="filter-field-label" style={{ marginBottom: 4 }}>{label}</div>
      <input
        className="input"
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled}
      />
    </div>
  );
}

function CompetitorBlock({ label, subtitle, min, index, ourPrice }: { label: string; subtitle: string; min: string | null | undefined; index: string | null | undefined; ourPrice: string | null | undefined }) {
  const m = min ? Number(min) : 0;
  const o = ourPrice ? Number(ourPrice) : 0;
  if (!m || m <= 0) return (
    <div style={{ padding: 14, background: 'var(--bg)', borderRadius: 8 }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
      <div className="muted">—</div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{subtitle}</div>
    </div>
  );
  const color = o > 0 && m < o ? 'var(--red)' : o > 0 && m > o ? 'var(--green)' : 'var(--text)';
  const diff = o > 0 ? ((m - o) / o) * 100 : 0;
  return (
    <div style={{ padding: 14, background: 'var(--bg)', borderRadius: 8 }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{m.toFixed(0)} ₽</div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
        {subtitle} {index && `· индекс ${Number(index).toFixed(2)}`}
        {o > 0 && (
          <> · <span style={{ color }}>{diff > 0 ? '+' : ''}{diff.toFixed(0)}%</span></>
        )}
      </div>
    </div>
  );
}

function AttributesTable({ attributes }: { attributes: { id: number; name: string | null; values: string[] }[] }) {
  const filtered = useMemo(() =>
    attributes.filter((a) => a.values.length > 0 && a.values[0] && a.values[0].length < 200), [attributes]);
  return (
    <table className="products" style={{ marginTop: 12, fontSize: 12 }}>
      <thead><tr><th style={{ width: 280 }}>Атрибут</th><th>Значение</th></tr></thead>
      <tbody>
        {filtered.map((a) => (
          <tr key={a.id}>
            <td>{a.name ?? <code>id={a.id}</code>}</td>
            <td>{a.values.join(', ')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SourceBadge({ source }: { source: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    strategy: { label: 'Стратегия', cls: 'badge-info' },
    rrp_protect: { label: 'РРЦ-защита', cls: 'badge-warn' },
    manual: { label: 'Вручную', cls: 'badge-success' },
    sync: { label: 'Sync', cls: 'badge' },
  };
  const m = map[source] ?? { label: source, cls: 'badge' };
  return <span className={`badge ${m.cls}`}>{m.label}</span>;
}
